import { Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import React, { useEffect, useRef } from 'react'

import styles from './MileageMap.style'

export interface MileagePlace {
  placeId: string
  name: string
  lat: number
  lng: number
}

export type MileageLocation = MileagePlace | string

export interface RouteComputedData {
  miles: number
  encodedPolyline: string
  resolvedOrigin: { lat: number; lng: number }
  resolvedDestination: { lat: number; lng: number }
  resolvedWaypoints: Array<{ lat: number; lng: number }>
}

interface MileageMapProps {
  origin?: MileageLocation
  destination?: MileageLocation
  waypoints: MileageLocation[]
  onRouteComputed: (data: RouteComputedData) => void
  onRouteError?: () => void
}

const NYC_CENTER = { lat: 40.7128, lng: -74.006 }
const DEFAULT_ZOOM = 12
const MAP_ID = 'mileage-map'
const METERS_PER_MILE = 1609.344
const ROUTE_DEBOUNCE_MS = 500
const ROUTE_FIELDS = ['distanceMeters', 'legs', 'path', 'viewport']
const POLYLINE_OPTIONS: google.maps.PolylineOptions = {
  strokeColor: '#1A73E8',
  strokeWeight: 5,
  strokeOpacity: 0.9,
}

const computeInitialCenter = (
  origin?: MileageLocation,
  destination?: MileageLocation,
  waypoints: MileageLocation[] = [],
): google.maps.LatLngLiteral => {
  const places = [origin, destination, ...waypoints].filter(
    (loc): loc is MileagePlace => !!loc && typeof loc !== 'string',
  )
  if (places.length === 0) return NYC_CENTER
  const lat = places.reduce((sum, p) => sum + p.lat, 0) / places.length
  const lng = places.reduce((sum, p) => sum + p.lng, 0) / places.length
  return { lat, lng }
}

const MileageMap: React.FC<MileageMapProps> = (props) => {
  const initialCenterRef = useRef<google.maps.LatLngLiteral | null>(null)
  if (initialCenterRef.current === null) {
    initialCenterRef.current = computeInitialCenter(
      props.origin,
      props.destination,
      props.waypoints,
    )
  }

  return (
    <div css={styles} className="MileageMap">
      <Map
        mapId={MAP_ID}
        defaultCenter={initialCenterRef.current}
        defaultZoom={DEFAULT_ZOOM}
        gestureHandling="greedy"
        disableDefaultUI={true}
        zoomControl={true}
      >
        <RouteLayer {...props} />
      </Map>
    </div>
  )
}

const toRoutesLocation = (
  loc: MileageLocation,
): google.maps.LatLngLiteral | string => {
  if (typeof loc === 'string') return loc
  return { lat: loc.lat, lng: loc.lng }
}

const RouteLayer: React.FC<MileageMapProps> = ({
  origin,
  destination,
  waypoints,
  onRouteComputed,
  onRouteError,
}) => {
  const map = useMap()
  const routesLib = useMapsLibrary('routes')
  const geometryLib = useMapsLibrary('geometry')
  const markerLib = useMapsLibrary('marker')

  const onRouteComputedRef = useRef(onRouteComputed)
  useEffect(() => {
    onRouteComputedRef.current = onRouteComputed
  }, [onRouteComputed])

  const onRouteErrorRef = useRef(onRouteError)
  useEffect(() => {
    onRouteErrorRef.current = onRouteError
  }, [onRouteError])

  const polylinesRef = useRef<google.maps.Polyline[]>([])
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([])

  const clearOverlays = (): void => {
    polylinesRef.current.forEach((p) => p.setMap(null))
    polylinesRef.current = []
    markersRef.current.forEach((m) => {
      m.map = null
    })
    markersRef.current = []
  }

  useEffect(() => {
    return clearOverlays
  }, [])

  const waypointsKey = waypoints
    .map((w) => (typeof w === 'string' ? w : w.placeId))
    .join('|')

  useEffect(() => {
    if (
      !map ||
      !routesLib ||
      !geometryLib ||
      !markerLib ||
      !origin ||
      !destination
    ) {
      return
    }

    let cancelled = false

    const timer = setTimeout(() => {
      routesLib.Route.computeRoutes({
        origin: toRoutesLocation(origin),
        destination: toRoutesLocation(destination),
        intermediates: waypoints.map((wp) => ({
          location: toRoutesLocation(wp),
        })),
        travelMode: 'DRIVING',
        fields: ROUTE_FIELDS,
      })
        .then(async (result) => {
          if (cancelled) return

          const route = result.routes?.[0]
          if (!route) {
            onRouteErrorRef.current?.()
            return
          }

          clearOverlays()

          const polylines = route.createPolylines({
            polylineOptions: POLYLINE_OPTIONS,
          })
          polylines.forEach((p) => p.setMap(map))
          polylinesRef.current = polylines

          try {
            const markers = await route.createWaypointAdvancedMarkers()
            if (cancelled) {
              markers.forEach((m) => {
                m.map = null
              })
              return
            }
            markers.forEach((m) => {
              m.map = map
            })
            markersRef.current = markers
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[MileageMap] waypoint markers failed', err)
          }

          if (route.viewport) {
            map.fitBounds(route.viewport)
          }

          const totalMeters = route.distanceMeters ?? 0
          const miles = Math.round((totalMeters / METERS_PER_MILE) * 10) / 10

          const path = route.path ?? []
          const encodedPolyline =
            path.length > 0
              ? geometryLib.encoding.encodePath(
                  path.map((p) => ({ lat: p.lat, lng: p.lng })),
                )
              : ''

          const legs = route.legs ?? []
          const firstLeg = legs[0]
          const lastLeg = legs[legs.length - 1]
          const resolvedOrigin = {
            lat: firstLeg?.startLocation?.lat ?? 0,
            lng: firstLeg?.startLocation?.lng ?? 0,
          }
          const resolvedDestination = {
            lat: lastLeg?.endLocation?.lat ?? 0,
            lng: lastLeg?.endLocation?.lng ?? 0,
          }
          const resolvedWaypoints = legs.slice(0, -1).map((leg) => ({
            lat: leg.endLocation?.lat ?? 0,
            lng: leg.endLocation?.lng ?? 0,
          }))

          onRouteComputedRef.current({
            miles,
            encodedPolyline,
            resolvedOrigin,
            resolvedDestination,
            resolvedWaypoints,
          })
        })
        .catch((err) => {
          if (cancelled) return
          // eslint-disable-next-line no-console
          console.error('[MileageMap] computeRoutes failed', err)
          onRouteErrorRef.current?.()
        })
    }, ROUTE_DEBOUNCE_MS)

    return (): void => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [
    map,
    routesLib,
    geometryLib,
    markerLib,
    origin,
    destination,
    waypointsKey,
  ])

  return null
}

export default MileageMap
